import React, { useEffect, useRef } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import {
  FaRobot,
  FaMicrochip,
  FaWind,
  FaSearch,
  FaPaintBrush,
  FaCheck,
} from "react-icons/fa";

function ModelsSectionComponent() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll(".animate-on-scroll");
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <section
      id="models"
      className="section-spacing landing-dark-background "
      ref={sectionRef}
    >
      <Container>
        <div className="text-center mb-5">
          <h2 className="landing-page-text fw-bold mb-3">
            Powerful AI Models at Your Fingertips
          </h2>
          <p className="lead text-muted mx-auto " style={{ maxWidth: "700px" }}>
            Access the world's most advanced AI models through a single, unified
            interface. Choose the right model for your specific needs.
          </p>
        </div>
        <Row className="g-4">
          {/* ChatGPT Card */}
          <Col md={6} lg={4}>
            <div className="animate-on-scroll">
              <Card className="h-100 border-0 model-card ">
                <Card.Body className="p-4 landing-dark-background">
                  <div className="d-flex align-items-center mb-3">
                    <span className="bg-success bg-opacity-10 p-3 rounded-circle me-3">
                      <FaRobot className="text-success fs-4" />
                    </span>
                    <h3 className="h4 mb-0 landing-page-text">ChatGPT</h3>
                  </div>
                  <p className="text-muted">
                    OpenAI's powerful language model for natural conversations,
                    content creation, and complex problem-solving.
                  </p>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <FaCheck className="text-success me-2 " /> Excellent for
                      general queries
                    </li>
                    <li className="mb-2">
                      <FaCheck className="text-success me-2 " /> Creative
                      writing assistance
                    </li>
                    <li className="mb-2">
                      <FaCheck className="text-success me-2 " /> Code and
                      technical support
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </div>
          </Col>

          {/* Gemini Card */}
          <Col md={6} lg={4}>
            <div className="animate-on-scroll">
              <Card className="h-100 border-0 model-card">
                <Card.Body className="p-4 landing-dark-background">
                  <div className="d-flex align-items-center mb-3">
                    <span className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
                      <FaMicrochip className="text-primary fs-4" />
                    </span>
                    <h3 className="h4 mb-0 landing-page-text">Gemini</h3>
                  </div>
                  <p className="text-muted">
                    Google's multimodal AI model excelling at reasoning, code
                    generation, and contextual understanding.
                  </p>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> Strong reasoning
                      capabilities
                    </li>
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> Excellent at
                      code generation
                    </li>
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> Better factual
                      accuracy
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </div>
          </Col>

          {/* Mistral Card */}
          <Col md={6} lg={4}>
            <div className="animate-on-scroll">
              <Card className="h-100 border-0 model-card">
                <Card.Body className="p-4 landing-dark-background">
                  <div className="d-flex align-items-center mb-3">
                    <span className="bg-danger bg-opacity-10 p-3 rounded-circle me-3">
                      <FaWind className="text-danger fs-4" />
                    </span>
                    <h3 className="h4 mb-0 landing-page-text">Mistral</h3>
                  </div>
                  <p className="text-muted">
                    An efficient, high-performance language model with
                    impressive capabilities for its size.
                  </p>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> Fast response
                      time
                    </li>
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> Excellent
                      instruction following
                    </li>
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> Great technical
                      knowledge
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </div>
          </Col>

          {/* Web Search Card */}
          <Col md={6} lg={6}>
            <div className="animate-on-scroll">
              <Card className="h-100 border-0 model-card">
                <Card.Body className="p-4 landing-dark-background">
                  <div className="d-flex align-items-center mb-3">
                    <span className="bg-info bg-opacity-10 p-3 rounded-circle me-3">
                      <FaSearch className="text-info fs-4" />
                    </span>
                    <h3 className="h4 mb-0 landing-page-text">Web Search</h3>
                  </div>
                  <p className="text-muted">
                    AI-powered web search that provides current information with
                    citations and references.
                  </p>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> Access to
                      current information
                    </li>
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> Fact-checking
                      with citations
                    </li>
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> Research
                      assistance with references
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </div>
          </Col>

          {/* AI Art Generation Card */}
          <Col md={6} lg={6}>
            <div className="animate-on-scroll">
              <Card className="h-100 border-0 model-card">
                <Card.Body className="p-4 landing-dark-background">
                  <div className="d-flex align-items-center mb-3">
                    <span className="bg-warning bg-opacity-10 p-3 rounded-circle me-3">
                      <FaPaintBrush className="text-warning fs-4" />
                    </span>
                    <h3 className="h4 mb-0 landing-page-text">
                      AI Art Generation
                    </h3>
                  </div>
                  <p className="text-muted">
                    Create stunning images, illustrations, and designs from
                    simple text descriptions.
                  </p>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> High-quality
                      image generation
                    </li>
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> Customizable art
                      styles
                    </li>
                    <li className="mb-2">
                      <FaCheck className="text-success me-2" /> Rapid creation
                      of visual concepts
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </section>    
  );
}

export default ModelsSectionComponent;
